/**
 * Import de recetas por URL a partir de metadatos schema.org/Recipe embebidos
 * como JSON-LD (`<script type="application/ld+json">`).
 *
 * DETERMINISTA y SIN IA: sólo lee lo que la web ya publica de forma estructurada
 * (el estándar que consumen Google/Pinterest). El backend actúa de proxy que
 * trae el HTML (evita CORS + guarda SSRF); TODO el mapeo schema.org → borrador
 * de receta vive aquí, en lógica pura testeable sin red ni DOM.
 *
 * El borrador se rellena en el formulario de "Nueva receta" para que el usuario
 * revise/edite antes de guardar — nunca se persiste una receta sin confirmación.
 */
import { normalizeUnit } from "../shopping/units";

export interface ImportedIngredient {
  name: string;
  quantity: number | null;
  unit: string | null;
}

export interface ImportedNutrition {
  calories: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
}

export interface ImportedRecipeDraft {
  name: string;
  servings: number | null;
  ingredients: ImportedIngredient[];
  steps: string[];
  tags: string[];
  notes: string | null;
  photoUrl: string | null;
  nutrition: ImportedNutrition;
}

type JsonObject = Record<string, unknown>;

const MAX_INGREDIENTS = 100;
const MAX_STEPS = 100;
const MAX_TAGS = 30;
const ROUND = 100;

/** Fracciones unicode habituales en recetas (½, ¾, ⅓…) a su valor decimal. */
const VULGAR_FRACTIONS: Record<string, number> = {
  "¼": 0.25,
  "½": 0.5,
  "¾": 0.75,
  "⅓": 1 / 3,
  "⅔": 2 / 3,
  "⅕": 0.2,
  "⅖": 0.4,
  "⅗": 0.6,
  "⅘": 0.8,
  "⅙": 1 / 6,
  "⅚": 5 / 6,
  "⅛": 0.125,
  "⅜": 0.375,
  "⅝": 0.625,
  "⅞": 0.875,
};

function round2(value: number): number {
  return Math.round(value * ROUND) / ROUND;
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Primer string no vacío de un valor que puede ser string, número o array. */
function firstString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstString(item);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * Extrae el contenido crudo de cada bloque `<script type="application/ld+json">`.
 * Regex (no DOM) para poder testear en node/bun sin jsdom; tolerante al orden de
 * atributos y a espacios.
 */
export function extractJsonLdBlocks(html: string): string[] {
  const blocks: string[] = [];
  const re =
    /<script\b[^>]*\btype\s*=\s*["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script\s*>/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html)) !== null) {
    const raw = match[1];
    if (raw && raw.trim()) {
      blocks.push(raw.trim());
    }
  }
  return blocks;
}

/** Parsea cada bloque y aplana `@graph`/arrays a una lista de nodos objeto. */
export function parseJsonLdNodes(blocks: string[]): JsonObject[] {
  const nodes: JsonObject[] = [];
  const push = (value: unknown): void => {
    if (Array.isArray(value)) {
      for (const item of value) {
        push(item);
      }
      return;
    }
    if (isObject(value)) {
      nodes.push(value);
      if (Array.isArray(value["@graph"])) {
        push(value["@graph"]);
      }
    }
  };
  for (const block of blocks) {
    try {
      push(JSON.parse(block));
    } catch {
      // Bloque JSON-LD malformado: se ignora, no rompe el import.
    }
  }
  return nodes;
}

function typeMatchesRecipe(node: JsonObject): boolean {
  const type = node["@type"];
  if (typeof type === "string") {
    return type.toLowerCase() === "recipe";
  }
  if (Array.isArray(type)) {
    return type.some((t) => typeof t === "string" && t.toLowerCase() === "recipe");
  }
  return false;
}

/** Encuentra el primer nodo cuyo `@type` sea (o incluya) "Recipe". */
export function findRecipeNode(nodes: JsonObject[]): JsonObject | null {
  return nodes.find(typeMatchesRecipe) ?? null;
}

/**
 * Cantidad al inicio de un texto de ingrediente. Soporta enteros, decimales
 * (`,`/`.`), fracciones (`1/2`), mixtas (`1 1/2`), fracciones unicode (`1½`) y
 * rangos (`2-3` → primer valor). Devuelve la cantidad y el resto del texto.
 */
export function parseLeadingQuantity(text: string): { quantity: number | null; rest: string } {
  const trimmed = text.trim();

  // Entero/decimal (posiblemente seguido de fracción unicode: "1 ½", "1½").
  const decimal = /^(\d+(?:[.,]\d+)?)\s*([¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])?/.exec(trimmed);
  const leadingFraction = /^([¼½¾⅓⅔⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])/.exec(trimmed);
  const asciiFraction = /^(\d+)\s+(\d+)\s*\/\s*(\d+)/.exec(trimmed); // mixta "1 1/2"
  const simpleFraction = /^(\d+)\s*\/\s*(\d+)/.exec(trimmed); // "1/2"

  if (asciiFraction) {
    const whole = Number(asciiFraction[1]);
    const num = Number(asciiFraction[2]);
    const den = Number(asciiFraction[3]);
    const value = den === 0 ? whole : whole + num / den;
    return { quantity: round2(value), rest: trimmed.slice(asciiFraction[0].length).trim() };
  }
  if (simpleFraction) {
    const num = Number(simpleFraction[1]);
    const den = Number(simpleFraction[2]);
    if (den !== 0) {
      return { quantity: round2(num / den), rest: trimmed.slice(simpleFraction[0].length).trim() };
    }
  }
  if (decimal) {
    let value = Number((decimal[1] ?? "").replace(",", "."));
    const vulgar = decimal[2];
    const vulgarValue = vulgar ? VULGAR_FRACTIONS[vulgar] : undefined;
    if (vulgarValue !== undefined) {
      value += vulgarValue;
    }
    if (Number.isFinite(value) && value > 0) {
      return { quantity: round2(value), rest: trimmed.slice(decimal[0].length).trim() };
    }
  }
  if (leadingFraction) {
    const key = leadingFraction[1];
    const value = key === undefined ? undefined : VULGAR_FRACTIONS[key];
    if (value !== undefined) {
      return { quantity: round2(value), rest: trimmed.slice(leadingFraction[0].length).trim() };
    }
  }
  return { quantity: null, rest: trimmed };
}

/**
 * Parte una línea libre ("200 g harina", "2 huevos", "Sal al gusto") en
 * {cantidad, unidad, nombre}. La unidad sólo se separa si el primer token tras
 * la cantidad es una unidad RECONOCIDA (familia != "other"); si no, se deja como
 * parte del nombre para no inventar unidades.
 */
export function parseIngredientLine(line: string): ImportedIngredient {
  const cleaned = line.replaceAll(/\s+/g, " ").trim();
  if (!cleaned) {
    return { name: "", quantity: null, unit: null };
  }
  const { quantity, rest } = parseLeadingQuantity(cleaned);
  if (quantity === null) {
    return { name: cleaned, quantity: null, unit: null };
  }

  const spaceIdx = rest.indexOf(" ");
  const firstToken = spaceIdx === -1 ? rest : rest.slice(0, spaceIdx);
  const remainder = spaceIdx === -1 ? "" : rest.slice(spaceIdx + 1).trim();
  const normalized = normalizeUnit(firstToken);
  const isKnownUnit = firstToken.length > 0 && normalized.family !== "other";

  if (isKnownUnit && remainder) {
    return { name: remainder, quantity, unit: firstToken.toLowerCase() };
  }
  return { name: rest, quantity, unit: null };
}

/** `recipeYield` → nº de porciones (primer entero encontrado). */
export function parseServings(value: unknown): number | null {
  const raw = firstString(value);
  if (!raw) {
    return null;
  }
  const match = /\d+/.exec(raw);
  if (!match) {
    return null;
  }
  const parsed = Number.parseInt(match[0], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * `recipeInstructions` → lista de pasos. Soporta string plano (párrafos), array
 * de strings, array de `HowToStep` ({text}) y `HowToSection` con
 * `itemListElement` anidado.
 */
export function parseInstructions(value: unknown): string[] {
  const steps: string[] = [];
  const visit = (item: unknown): void => {
    if (steps.length >= MAX_STEPS) {
      return;
    }
    if (typeof item === "string") {
      for (const part of item.split(/\r?\n+/)) {
        const trimmed = part.trim();
        if (trimmed) {
          steps.push(trimmed);
        }
      }
      return;
    }
    if (Array.isArray(item)) {
      for (const child of item) {
        visit(child);
      }
      return;
    }
    if (isObject(item)) {
      // HowToSection: sus pasos cuelgan de itemListElement; su `name` es el
      // título de la sección, NO un paso, así que no se emite.
      if (item.itemListElement !== undefined) {
        visit(item.itemListElement);
        return;
      }
      const text = firstString(item.text) ?? firstString(item.name);
      if (text) {
        steps.push(text);
      }
    }
  };
  visit(value);
  return steps.slice(0, MAX_STEPS);
}

/** `keywords`/`recipeCategory`/`recipeCuisine` → etiquetas normalizadas. */
export function parseKeywords(value: unknown): string[] {
  const raw: string[] = [];
  if (typeof value === "string") {
    raw.push(...value.split(","));
  } else if (Array.isArray(value)) {
    for (const item of value) {
      const s = firstString(item);
      if (s) {
        raw.push(s);
      }
    }
  }
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const item of raw) {
    const tag = item.trim();
    const key = tag.toLowerCase();
    if (tag && !seen.has(key)) {
      seen.add(key);
      tags.push(tag);
    }
  }
  return tags.slice(0, MAX_TAGS);
}

/** `image` → primera URL (string, {url}, o array de cualquiera de ambos). */
export function parseImageUrl(value: unknown): string | null {
  if (typeof value === "string") {
    return value.trim() || null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const url = parseImageUrl(item);
      if (url) {
        return url;
      }
    }
    return null;
  }
  if (isObject(value)) {
    return firstString(value.url) ?? firstString(value.contentUrl);
  }
  return null;
}

/** Primer número dentro de un texto tipo "240 kcal" / "12 g" / 12. */
function firstNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? round2(value) : null;
  }
  if (typeof value === "string") {
    const match = /\d+(?:[.,]\d+)?/.exec(value);
    if (match) {
      const parsed = Number(match[0].replace(",", "."));
      return Number.isFinite(parsed) ? round2(parsed) : null;
    }
  }
  return null;
}

/** `nutrition` (NutritionInformation) → macros por porción. */
export function parseNutrition(value: unknown): ImportedNutrition {
  const empty: ImportedNutrition = { calories: null, proteinG: null, carbsG: null, fatG: null };
  if (!isObject(value)) {
    return empty;
  }
  return {
    calories: firstNumber(value.calories),
    proteinG: firstNumber(value.proteinContent),
    carbsG: firstNumber(value.carbohydrateContent),
    fatG: firstNumber(value.fatContent),
  };
}

/** Mapea un nodo schema.org/Recipe a un borrador de receta. */
export function parseRecipeFromJsonLd(node: JsonObject): ImportedRecipeDraft {
  const ingredientsRaw = node.recipeIngredient ?? node.ingredients;
  const ingredients: ImportedIngredient[] = [];
  if (Array.isArray(ingredientsRaw)) {
    for (const item of ingredientsRaw) {
      const line = firstString(item);
      if (line) {
        ingredients.push(parseIngredientLine(line));
      }
      if (ingredients.length >= MAX_INGREDIENTS) {
        break;
      }
    }
  } else {
    const single = firstString(ingredientsRaw);
    if (single) {
      ingredients.push(parseIngredientLine(single));
    }
  }

  const tags = parseKeywords(node.keywords);
  if (tags.length === 0) {
    tags.push(...parseKeywords(node.recipeCategory));
  }

  return {
    name: firstString(node.name) ?? "",
    servings: parseServings(node.recipeYield ?? node.yield),
    ingredients,
    steps: parseInstructions(node.recipeInstructions),
    tags,
    notes: firstString(node.description),
    photoUrl: parseImageUrl(node.image),
    nutrition: parseNutrition(node.nutrition),
  };
}

/**
 * Punto de entrada: HTML de una página de receta → borrador, o `null` si no hay
 * ningún nodo schema.org/Recipe utilizable (sin nombre y sin ingredientes).
 */
export function parseRecipeFromHtml(html: string): ImportedRecipeDraft | null {
  const nodes = parseJsonLdNodes(extractJsonLdBlocks(html));
  const recipeNode = findRecipeNode(nodes);
  if (!recipeNode) {
    return null;
  }
  const draft = parseRecipeFromJsonLd(recipeNode);
  if (!draft.name && draft.ingredients.length === 0) {
    return null;
  }
  return draft;
}
